import { StrictMode, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import { createHttpTransport } from "@tdelivr/api-client";

import { arrivalFor, LeavingForTheProvider } from "./auth/arrival.tsx";
import { ConsoleAuthority, type SignInFailure } from "./auth/authority.ts";
import { isConfigured, readOidcConfiguration } from "./auth/configuration.ts";
import { useAuthentication } from "./auth/use-authentication.ts";
import { developmentSession } from "./development-session.ts";
import { platformPages } from "./navigation.ts";
import { pageIdIn, urlForPage } from "./page-location.ts";
import type { Session } from "./session.ts";
import { DataCatalogue } from "./ui/DataCatalogue.tsx";
import { EventCatalogue } from "./ui/EventCatalogue.tsx";
import { EventReplays } from "./ui/EventReplays.tsx";
import { EventSchemas } from "./ui/EventSchemas.tsx";
import { GatewayRoutes } from "./ui/GatewayRoutes.tsx";
import { ModelServing } from "./ui/ModelServing.tsx";
import { PageOutlook } from "./ui/PageOutlook.tsx";
import { PartnerApi } from "./ui/PartnerApi.tsx";
import { PlatformAudit } from "./ui/PlatformAudit.tsx";
import { PlansEntitlements } from "./ui/PlansEntitlements.tsx";
import { PlatformFlags } from "./ui/PlatformFlags.tsx";
import { RiskFraud } from "./ui/RiskFraud.tsx";
import { SearchIndex } from "./ui/SearchIndex.tsx";
import { SessionEnded } from "./ui/SessionEnded.tsx";
import { Shell } from "./ui/Shell.tsx";
import { SignIn } from "./ui/SignIn.tsx";
import { SignOutDialog } from "./ui/SignOutDialog.tsx";
import { SloTelemetry } from "./ui/SloTelemetry.tsx";
import { StateView } from "./ui/StateView.tsx";
import { Tenants } from "./ui/Tenants.tsx";
import { WorkflowSchedules } from "./ui/WorkflowSchedules.tsx";
import "./styles.css";

/**
 * The platform super-admin console, running.
 *
 * Two ways in, and only one of them exists in a production build — the arrangement the other three
 * consoles use, copied rather than shared for the reason
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` records.
 *
 * **Four pages are routed, of the sixteen the design draws, and that is the honest number rather
 * than a stopping point.** `tests/contract/console-screen-support.test.ts` measures this cohort
 * screen by screen against `docs/design/api-operations.json`, and the per-screen table names the
 * drawn element behind every absence. Drawing a blocked screen means inventing the rows it is a
 * board of, which `CLAUDE.md` section 2 forbids — so the rest fall through to `PageOutlook`, which
 * says what each is waiting on in the measurement's own words.
 *
 * Each of the four reads **one** listing that crosses the tenancy boundary on purpose, and in each
 * case that listing is a second operation beside an untouched tenant-bounded one:
 *
 * | page      | listing                                     | scope                               |
 * | --------- | ------------------------------------------- | ----------------------------------- |
 * | `audit`   | `AUD-01 GET /v1/platform/audit-records`     | `audit:platform:read` (ADR 0039)    |
 * | `gateway` | `EXP-001 GET /v1/platform/gateway/routes`   | `gateway:platform:read` (ADR 0051)  |
 * | `partner` | `API-02 GET /v1/platform/partner-credentials` | `partner-credentials:platform:read` (ADR 0052) |
 * | `pflags`  | `CFG-02 GET /v1/platform/features`          | `feature-management:platform:read` (ADR 0053) |
 *
 * The last three were served because ADR 0048 made a rule of what ADR 0039 had decided once: a page
 * titled `Platform …` may not be drawn from a listing whose own contract bounds it to the signed-in
 * tenant, and each of those three had exactly such a listing — reachable, public, parameterless, and
 * answering the wrong rows.
 *
 * **There is no BFF behind this, and that is a recorded decision rather than an omission.**
 * Document 05 §163 catalogues EXP-005, the Platform Admin BFF, and
 * [ADR 0047](../../../docs/adr/0047-the-platform-console-calls-aud-01-directly-and-exp-005-is-not-built-yet.md)
 * declines to build it: each board this console draws makes **one call to one owner**, so an
 * aggregator would aggregate nothing and add a hop and a deployment unit per board. That is EXP-004's
 * own refusal applied to a smaller surface, and the ADR states the condition under which it reverses
 * — a screen that needs more than one owner's answer. None of these four does; each reads one
 * listing from one service, which is why four boards rather than one does not move that decision.
 */

/**
 * Platform, then group, then page.
 *
 * The design's own crumbs, computed in its `_rv()` as
 * `['Platform', NAV.find(g => g[1].some(x => x[0] === s.page))[0], t]` — three deep, with the word
 * "Platform" where the other consoles put the tenant's name. That is not this console being
 * inconsistent: a trail here cannot start with a tenant, because the board spans every tenant, and
 * the design agrees by starting it with the console's own scope instead.
 */
function crumbsFor(pageId: string): readonly string[] {
  const page = platformPages.find((candidate) => candidate.id === pageId);
  return ["Platform", page?.section ?? "Platform", page?.title ?? "Unknown page"];
}

/**
 * What every drawn board takes, which is the same three things.
 *
 * The sameness is not a coincidence to be relied on loosely: each board reads its own operations and
 * so needs the transport, each guards its own scopes and so needs the session, and each draws the
 * design's three-deep trail and so needs the crumbs. Nothing else is passed, and in particular no
 * board is handed data fetched by this file — a board that cannot say which operation filled a cell
 * is a board that can draw a cell nothing answers.
 */
interface BoardProps {
  readonly transport: ReturnType<typeof createHttpTransport>;
  readonly session: Session;
  readonly crumbs: readonly string[];
}

/**
 * Which component draws each page, for the fourteen of sixteen that are drawn.
 *
 * This was a chain of `pageId === "…" ? <X/> :` down to a fallback, and that read well at four
 * branches. At thirteen it had stopped reading at all, and the shape of the thing it encodes — one
 * page identifier, one component, one prop shape — is a table. A page absent from this table falls
 * to `PageOutlook`, which is the design's own "not built yet" state, so the three undrawn pages need
 * no entry and no branch.
 *
 * The keys are `navigation.ts`'s page ids and `tests/page-support.test.ts` holds them to the pages
 * that platform operations can actually answer, so a page routed here while still recorded as
 * blocked fails that test rather than shipping a board with nothing behind it.
 */
const BOARDS: Readonly<Record<string, (props: BoardProps) => ReactNode>> = {
  /*
   * First, because it is the design's own first rail item and because every other page on this
   * console needs an identifier it supplies. It was the last to be routed, since
   * `POST /v1/tenants` existed and `GET /v1/tenants` did not until TEN-01 published it.
   */
  tenants: Tenants,
  audit: PlatformAudit,
  gateway: GatewayRoutes,
  partner: PartnerApi,
  pflags: PlatformFlags,
  schemas: EventSchemas,
  dlq: EventCatalogue,
  replay: EventReplays,
  search: SearchIndex,
  workflow: WorkflowSchedules,
  risk: RiskFraud,
  data: DataCatalogue,
  ml: ModelServing,
  slo: SloTelemetry,
  /*
   * **ADR 0092, and the one page on this console whose recorded reason named a route that has
   * since been published and still did not help.**
   *
   * It said the `Tenants` column needed `GET /v1/tenants`. ADR 0083 published exactly that, and
   * the column is still empty — because what it counts is tenants **per plan**, and nothing records
   * which plan a tenant is on. The first of its three claims was the real one: nothing modelled a
   * plan, and now TEN-03 does.
   */
  plans: PlansEntitlements,
};

interface ConsoleProps {
  readonly session: Session;
  /**
   * The bearer token for the next request, or undefined when there is no token to send.
   *
   * A function rather than a string, and asynchronous, because that is how a token stays fresh: the
   * authority refreshes one that is about to expire before handing it over, so a request never goes
   * out carrying a token a service will refuse.
   *
   * Absent on the development path, which has no token at all. That is what the fidelity harness
   * runs with: the stubs answer without looking at an authorization header.
   */
  readonly accessToken?: () => Promise<string | undefined>;
  readonly onSignOut?: () => void;
  /** Rendered in place of the page, with the chrome intact, as the design draws it. */
  readonly expired?: ReactNode;
}

function Console({ session, accessToken, onSignOut, expired }: ConsoleProps): ReactNode {
  const [pageId, setPageId] = useState<string>(() => pageIdIn(window.location.href));

  useEffect(() => {
    const onPopState = (): void => {
      setPageId(pageIdIn(window.location.href));
    };
    window.addEventListener("popstate", onPopState);
    return (): void => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const navigate = useCallback((next: string) => {
    window.history.pushState(null, "", urlForPage(window.location.href, next));
    setPageId(next);
  }, []);

  /*
   * One transport for the session, and memoised.
   *
   * `tenantId` is still supplied, and on this console that deserves a sentence. The transport sends
   * it as the tenant header every service validates, and
   * `tests/contract/tenant-context-required.test.ts` is why every request carries one — a request
   * with no tenant context is refused by the runtime before a route sees it. What the *platform*
   * audit read does with it is nothing: its own published description says results are not
   * restricted to the verified token's tenant. So the header is the caller identifying which tenant
   * it is acting for, and the operation is the one that spans them all; both are true at once, and a
   * transport that omitted it would be refused by the other 141 services for no benefit here.
   */
  const transport = useMemo(
    () =>
      createHttpTransport({
        baseUrl: import.meta.env.VITE_PLATFORM_URL ?? window.location.origin,
        tenantId: () => session.tenantId,
        ...(accessToken === undefined ? {} : { authorization: accessToken }),
      }),
    [session.tenantId, accessToken],
  );

  const Board = BOARDS[pageId];

  return (
    <Shell
      session={session}
      currentPageId={pageId}
      onNavigate={navigate}
      transport={transport}
      {...(onSignOut === undefined ? {} : { onSignOut })}
    >
      {expired !== undefined ? (
        expired
      ) : Board === undefined ? (
        <PageOutlook session={session} />
      ) : (
        <Board transport={transport} session={session} crumbs={crumbsFor(pageId)} />
      )}
    </Shell>
  );
}

/**
 * Everything above the console: leaving to sign in, the code exchange, the expiry and the sign-out.
 *
 * One switch over one state, which is the point of `ConsoleAuthority` holding all of it. Exactly one
 * of its branches leaves this page — `signed_out`, and only when `arrivalFor` says the arrival has
 * nothing to report. Every other branch draws something, which is ADR 0022's own caution: a state
 * that is unauthenticated *because something went wrong* must be read, not retried.
 */
function Authenticated({ authority }: { readonly authority: ConsoleAuthority }): ReactNode {
  const state = useAuthentication(authority);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [dismissedFailure, setDismissedFailure] = useState(false);

  const providerName = authority.providerName;
  const accessToken = useCallback(() => authority.accessToken(), [authority]);
  const signIn = useCallback(() => {
    setDismissedFailure(false);
    void authority.beginSignIn();
  }, [authority]);

  switch (state.kind) {
    case "starting":
      return (
        <div className="boot">
          <StateView state={{ kind: "loading" }} purpose="The console is starting." />
        </div>
      );

    case "signed_out":
      return arrivalFor(state).kind === "to_the_provider" ? (
        <LeavingForTheProvider authority={authority} />
      ) : (
        <SignIn
          step="start"
          afterSignOut={state.afterSignOut}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "authenticating":
      return (
        <SignIn
          step="authenticating"
          afterSignOut={false}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "failed":
      return (
        <SignIn
          step={dismissedFailure ? "start" : "failed"}
          afterSignOut={false}
          providerName={providerName}
          failure={state.failure}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "expired":
      return (
        <Console
          session={state.session}
          accessToken={accessToken}
          expired={
            <SessionEnded
              tenantName={state.session.tenantName}
              endedAt={state.endedAt}
              lastedMs={state.lastedMs}
              onSignInAgain={() => {
                void authority.beginSignIn();
              }}
            />
          }
        />
      );

    case "signed_in":
      return (
        <>
          <Console
            session={state.session}
            accessToken={accessToken}
            onSignOut={() => setConfirmingSignOut(true)}
          />
          {confirmingSignOut ? (
            <SignOutDialog
              onDismiss={() => setConfirmingSignOut(false)}
              onConfirm={() => {
                setConfirmingSignOut(false);
                void authority.signOut();
              }}
            />
          ) : null}
        </>
      );
  }
}

/** A build with no identity provider configured, said in the design's failure card. */
function unconfigured(missing: readonly string[]): SignInFailure {
  return {
    code: "console_not_configured",
    headline: "This console has no identity provider configured",
    detail: `It was built without ${missing.join(" and ")}, so there is nowhere to send you to sign in. A platform administrator sets those when the console is built or served. Retrying won't change this.`,
    retryable: false,
  };
}

const root = document.getElementById("root");
if (root === null) throw new Error("The console has no mount point");

/*
 * Rendered synchronously, in every case.
 *
 * Module scripts run before `DOMContentLoaded`, and the fidelity harness asks whether a navigation
 * button `isVisible()` immediately after that event without waiting for it — so a bootstrap that
 * awaited anything before its first render would have the harness conclude the rail was absent and
 * start opening a drawer that is not there at 1440.
 */
const development = import.meta.env.DEV ? developmentSession() : undefined;

if (development !== undefined) {
  createRoot(root).render(
    <StrictMode>
      <Console session={development} />
    </StrictMode>,
  );
} else {
  const configuration = readOidcConfiguration(import.meta.env, window.location.origin);
  if (!isConfigured(configuration)) {
    createRoot(root).render(
      <StrictMode>
        <SignIn
          step="failed"
          afterSignOut={false}
          providerName="your organisation's sign-in"
          failure={unconfigured(configuration.missing)}
          onContinue={() => {}}
          onStartAgain={() => {}}
        />
      </StrictMode>,
    );
  } else {
    const authority = new ConsoleAuthority({
      configuration,
      fetch: globalThis.fetch.bind(globalThis),
      crypto: globalThis.crypto,
      // Per tab. Two tabs signing in at once must not read each other's code verifier, and a
      // verifier left in `localStorage` would outlive the browser session it belongs to.
      storage: window.sessionStorage,
      currentUrl: (): string => window.location.href,
      replaceUrl: (url: string): void => {
        window.history.replaceState(null, "", url);
      },
      navigate: (url: string): void => {
        window.location.assign(url);
      },
      now: (): number => Date.now(),
    });

    createRoot(root).render(
      <StrictMode>
        <Authenticated authority={authority} />
      </StrictMode>,
    );
    void authority.start();
  }
}
